import { faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Input from 'components/inputs/Input';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import useUpsertDailyBudgetOverride from './useUpsertDailyBudgetOverride';

const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const MyInput = styled(Input)`
  padding-right: 5px;
  margin: 0;
  text-align: right;
  width: 50px;
  height: 20px;
`;

interface Props {
  value: number;
  date: Date;
  companyId: number;
  overrideId?: number;
  amount: number;
  transferredFromPreviousDay?: number;
  onUpdate?: () => void;
  isFocused?: boolean;
}

const PlannedAmountInput: FC<Props> = ({
  value,
  date,
  companyId,
  overrideId,
  amount,
  transferredFromPreviousDay,
  onUpdate,
  isFocused,
}) => {
  const [cellValue, setCellValue] = useState(value);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { upsert, isLoading } = useUpsertDailyBudgetOverride();

  // Keep the local input in sync if the upstream override changes (e.g. nightly job, refetch).
  useEffect(() => {
    setCellValue(value);
  }, [value]);

  const handleChangeValue = useCallback(
    (val: number) => {
      if (val !== value) {
        upsert(
          {
            companyId,
            date,
            overrideId,
            amount,
            transferredFromPreviousDay,
            plannedAmount: val,
          },
          onUpdate
        );
      }
    },
    [
      value,
      upsert,
      companyId,
      date,
      overrideId,
      amount,
      transferredFromPreviousDay,
      onUpdate,
    ]
  );

  useEffect(() => {
    if (isFocused) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = setTimeout(() => handleChangeValue(cellValue), 3000);
    }
  }, [cellValue, handleChangeValue, isFocused]);

  return (
    <Wrapper>
      {isLoading && <FontAwesomeIcon icon={faSpinner} spin />}
      {isFocused ? (
        <MyInput
          type="number"
          disabled={isLoading}
          value={cellValue}
          onChange={(eve) => {
            const newNum = Number(eve.target.value);
            if (!Number.isNaN(newNum) && newNum < 0) {
              setCellValue(0);
            } else {
              setCellValue(newNum);
            }
          }}
          onKeyDown={(eve) => {
            if (eve.key === 'Enter') {
              handleChangeValue(cellValue);
            }
          }}
        />
      ) : (
        <span title="Planerade" style={{ color: '#ca8a04' }}>
          {value}
        </span>
      )}
    </Wrapper>
  );
};

export default PlannedAmountInput;
